// Learner App does not support translation (see /learnerApp/src/App.tsx)
/* eslint-disable i18next/no-literal-string */
import { Box, Icon, OsStack, Theme, Typography } from "@outschool/backpack";
import { faSparkles } from "@outschool/icons";
import { useSnackbar } from "@outschool/ui-components-website";
import React, { useCallback, useState } from "react";
import { useAnalytics } from "use-analytics";

import { Topic, TopicFilter } from "./consts";
import MixFooter from "./MixFooter";
import MixTopicCard from "./MixTopicCard";
import MixTopicModal, { ModalMixTopic } from "./MixTopicModal";
import TopicFilterButton from "./TopicFilterButton";
import {
  GeneratedMixTopicResult,
  useGenerateMixTopic,
} from "./useGenerateMixTopic";
import useTopicsForFilter from "./useTopicsForFilter";

export default function MixUpMachineContent() {
  const { addSnackbarContent } = useSnackbar();
  const { track } = useAnalytics();

  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
  const [openModalTopic, setOpenModalTopic] = useState<ModalMixTopic | null>(
    null,
  );
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<TopicFilter>(
    TopicFilter.All,
  );

  const { data: topicsToShow } = useTopicsForFilter(
    selectedTopicFilter,
    selectedTopics,
  );

  const handleTopicGenerated = useCallback((topic: GeneratedMixTopicResult) => {
    setOpenModalTopic({
      title: topic.title,
      description: topic.description,
      imageUrl: "",
      sourceTopics: topic.sourceTopics,
      isDefault: false,
      isImageLoading: true,
    });
  }, []);

  const handleImageReady = useCallback((imageUrl: string) => {
    setOpenModalTopic(prev =>
      prev ? { ...prev, imageUrl, isImageLoading: false } : null,
    );
  }, []);

  const handleError = useCallback(() => {
    addSnackbarContent(
      <Typography variant="subtitle1">
        {"Oops, something went wrong while mixing! Please try again."}
      </Typography>,
      5000,
      "error",
    );
  }, [addSnackbarContent]);

  const { generateMixTopic, isGeneratingTopic, generatingError, reset } =
    useGenerateMixTopic({
      onTopicGenerated: handleTopicGenerated,
      onImageReady: handleImageReady,
      onError: handleError,
    });

  const onModalClose = () => {
    setOpenModalTopic(null);
    setSelectedTopics([]);
    reset();
  };

  const onModalSave = () => {
    if (selectedTopicFilter === TopicFilter.DefaultTopics) {
      setSelectedTopicFilter(TopicFilter.All);
    }
  };

  const onStartMixing = () => {
    const sourceTopics = selectedTopics.map(topic => topic.title);
    track("mixup_machine_generate_topic_click", {
      sourceTopics: sourceTopics,
    });
    generateMixTopic(sourceTopics);
  };

  const onClearAllTopics = () => {
    setSelectedTopics([]);
  };

  const onClearTopic = (uid: string) => {
    setSelectedTopics(selectedTopics.filter(t => t.uid !== uid));
  };

  return (
    <>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "column",
          gap: "24px",
          marginBottom: "4em",
          [theme.breakpoints.down("sm")]: {
            marginBottom: "5em",
          },
        })}
      >
        <Typography variant="h2">{"The Mix-Up Machine"}</Typography>
        <Typography variant="body1" sx={{ color: "grey.600" }}>
          {"Select 2 topics and mix them to explore your interests."}
        </Typography>
        <OsStack direction="row" spacing={2}>
          {Object.values(TopicFilter).map(filter => (
            <TopicFilterButton
              key={filter}
              filter={filter}
              selectedFilter={selectedTopicFilter}
              onFilterChange={setSelectedTopicFilter}
            />
          ))}
        </OsStack>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          {topicsToShow.map((topic: Topic) => (
            <MixTopicCard
              key={topic.uid}
              title={topic.title}
              imageUrl={topic.attachmentUrl}
              isDefault={topic.isDefault}
              isSelected={selectedTopics.includes(topic)}
              onCardClick={() => {
                if (selectedTopics.includes(topic)) {
                  setSelectedTopics(selectedTopics.filter(t => t !== topic));
                } else if (selectedTopics.length < 2) {
                  setSelectedTopics([...selectedTopics, topic]);
                } else {
                  return;
                }
              }}
              onButtonClick={() => {
                setOpenModalTopic({
                  uid: topic.uid,
                  title: topic.title,
                  description: topic.description,
                  imageUrl: topic.attachmentUrl,
                  isDefault: topic.isDefault,
                });
                track("mixup_machine_open_modal_click", {
                  topicUid: topic.uid,
                  topicTitle: topic.title,
                  isDefault: topic.isDefault,
                });
              }}
            />
          ))}
        </Box>
        <OsStack direction="row" spacing={2} alignItems="center">
          <Icon
            icon={faSparkles}
            sx={{ color: "warning.500", fontSize: "16px" }}
          />
          <Typography
            variant="body2"
            sx={{ color: "neutral.600", fontSize: "16px" }}
          >
            Content generated by AI. It can make mistakes or miss context.
          </Typography>
        </OsStack>
        <MixTopicModal
          topic={openModalTopic}
          onClose={onModalClose}
          onSave={onModalSave}
        />
      </Box>
      <MixFooter
        selectedTopics={selectedTopics}
        isGeneratingTopic={isGeneratingTopic}
        generatingTopicError={generatingError}
        onStartMixing={onStartMixing}
        onClearAllTopics={onClearAllTopics}
        onClearTopic={onClearTopic}
      />
    </>
  );
}
